import { create } from "zustand";

import type { NotificationsModels } from "@/data/notifications/notifications.models";

const isEndorsementNotification = (type: string) => {
  return type.toLowerCase().includes("endorsement");
};

interface NotificationStoreState {
  notifications: NotificationsModels.NotificationDto[];
  setNotifications: (notifications: NotificationsModels.NotificationDto[]) => void;
  addNotification: (notification: NotificationsModels.NotificationDto) => void;
  updateNotification: (id: string, updates: Partial<NotificationsModels.NotificationDto>) => void;
  deleteNotification: (id: string) => void;
  clearAllNotifications: () => void;
  appendNotifications: (notifications: NotificationsModels.NotificationDto[]) => void;
  getUnreadCount: () => number;
}

export const useNotificationStore = create<NotificationStoreState>((set, get) => ({
  notifications: [],

  setNotifications: (notifications) =>
    set({
      notifications: notifications.filter((n) => !isEndorsementNotification(n.type)),
    }),

  addNotification: (notification) => {
    // Filter out endorsement notifications
    if (isEndorsementNotification(notification.type)) {
      return;
    }

    set((state) => {
      // Check if notification already exists
      const exists = state.notifications.some((n) => n.id === notification.id);
      if (exists) {
        return state;
      }

      // Add new notification at the beginning (newest first)
      return {
        notifications: [notification, ...state.notifications],
      };
    });
  },

  updateNotification: (id, updates) =>
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, ...updates } : n)),
    })),

  deleteNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearAllNotifications: () => set({ notifications: [] }),

  appendNotifications: (notifications) =>
    set((state) => {
      // Filter out endorsements and duplicates
      const filtered = notifications.filter((n) => !isEndorsementNotification(n.type));
      const existingIds = new Set(state.notifications.map((n) => n.id));
      const newNotifications = filtered.filter((n) => !existingIds.has(n.id));

      return {
        notifications: [...state.notifications, ...newNotifications],
      };
    }),

  getUnreadCount: () => {
    const state = get();
    return state.notifications.filter((n) => !n.readAt).length;
  },
}));
